import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate, Outlet, redirect, useLocation } from "@tanstack/react-router";
import {
  ActivityIcon,
  FilesIcon,
  FolderGit2Icon,
  LayoutDashboardIcon,
  LockIcon,
  LogOutIcon,
  MenuIcon,
  MonitorSmartphoneIcon,
  PackageSearchIcon,
  ShieldCheckIcon,
  SlidersHorizontalIcon,
  UsersIcon,
} from "lucide-react";
import { useState, type SubmitEvent, type ReactNode } from "react";
import { Logo } from "@/components/logo";
import { ErrorText } from "@/components/page";
import { SyncBadge } from "@/components/sync-badge";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api, errorMessage } from "@/lib/api";
import { lockVault, logout, session, unlockWithPassword } from "@/lib/session";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app")({
  beforeLoad: ({ location }) => {
    if (session.get().status === "anon") {
      throw redirect({ to: "/login", search: { redirect: location.href, reason: undefined } });
    }
  },
  component: AppLayout,
});

const NAV: { to: string; label: string; icon: typeof ActivityIcon; admin?: boolean }[] = [
  { to: "/", label: "Overview", icon: LayoutDashboardIcon },
  { to: "/projects", label: "Projects", icon: FolderGit2Icon },
  { to: "/files", label: "Files", icon: FilesIcon },
  { to: "/inventory", label: "Inventory", icon: PackageSearchIcon },
  { to: "/activity", label: "Activity", icon: ActivityIcon },
  { to: "/devices", label: "Devices", icon: MonitorSmartphoneIcon },
  { to: "/rules", label: "Rules", icon: SlidersHorizontalIcon },
  { to: "/security", label: "Security", icon: ShieldCheckIcon },
  { to: "/admin", label: "Admin", icon: UsersIcon, admin: true },
];

function NavLinks({ onNavigate, isAdmin }: { onNavigate?: () => void; isAdmin: boolean }) {
  const { pathname } = useLocation();
  return (
    <nav className="flex flex-col gap-0.5">
      {NAV.filter((n) => !n.admin || isAdmin).map((n) => {
        const active = n.to === "/" ? pathname === "/" : pathname.startsWith(n.to);
        return (
          <Link
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            className={cn(
              "flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm transition-colors",
              active
                ? "bg-accent font-medium text-accent-foreground"
                : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
            )}
          >
            <n.icon className="size-4" />
            {n.label}
          </Link>
        );
      })}
    </nav>
  );
}

function AppLayout() {
  const s = useStore(session);
  const [open, setOpen] = useState(false);
  // Devices are needed almost everywhere to label activity; warm the cache here.
  useQuery({ queryKey: ["devices"], queryFn: () => api.devices.list(), enabled: s.status === "unlocked" });
  if (s.status === "anon") return <Navigate to="/login" search={{ redirect: undefined, reason: undefined }} />;
  if (s.status === "loading") return null;
  const isAdmin = s.account.role === "super_admin";

  const sidebar = (onNavigate?: () => void): ReactNode => (
    <div className="flex h-full flex-col gap-4">
      <NavLinks onNavigate={onNavigate} isAdmin={isAdmin} />
      <div className="mt-auto space-y-1 border-t pt-3 text-sm">
        <p className="truncate px-2.5 font-medium">{s.account.name}</p>
        <p className="truncate px-2.5 text-xs text-muted-foreground">{s.account.email}</p>
        <div className="flex gap-1 px-1 pt-1">
          <Button variant="ghost" size="sm" onClick={lockVault} title="Lock vault">
            <LockIcon /> Lock
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void logout()} title="Sign out">
            <LogOutIcon /> Sign out
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-full">
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r bg-sidebar p-3 md:flex">
        <div className="mb-4 px-1">
          <Logo />
        </div>
        {sidebar()}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/80 px-4 py-2 backdrop-blur sm:px-6">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger render={<IconButton label="Open menu" size="icon" className="md:hidden" />}>
              <MenuIcon />
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-3">
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="mb-4 px-1">
                <Logo />
              </div>
              {sidebar(() => {
                setOpen(false);
              })}
            </SheetContent>
          </Sheet>
          <div className="md:hidden">
            <Logo />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SyncBadge />
            <ThemeToggle />
          </div>
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6">
          {s.status === "unlocked" ? <Outlet /> : <LockedScreen />}
        </main>
      </div>
    </div>
  );
}

function LockedScreen() {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await unlockWithPassword(password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto mt-10 max-w-sm rounded-2xl border bg-card p-6" data-testid="locked-screen">
      <div className="mb-4 flex items-center gap-2">
        <LockIcon className="size-5" />
        <h1 className="text-lg font-semibold">Vault locked</h1>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        You are signed in, but this browser has no key for your vault yet. Enter your password to unlock it; future
        visits will unlock automatically.
      </p>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="unlock-password">Password</Label>
          <Input
            id="unlock-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <ErrorText error={error} />
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Unlocking…" : "Unlock"}
        </Button>
      </form>
    </div>
  );
}
